import ReactGA from 'react-ga4';
import mirror from '@rv-life/analytics-contract/mirror.json';
import { getRouteGroup } from './match-routes';

// Read from the contract's JSON mirror, not its main entry, to keep zod + the event catalog out of
// the shell bundle; a schema bump still propagates with no host edit.
const SCHEMA_VERSION = mirror.schema_version;
const PLATFORM = 'web';

let userAuthState: 'anon' | 'authed' = 'anon';

/** Set the coarse `user_auth_state` core dimension stamped on every GA4 event. */
export const setUserAuthState = (state: 'anon' | 'authed'): void => {
	userAuthState = state;
};

let analyticsReady = false;

type EventParams = Record<string, unknown>;

// Bridged remote events (and any emit) that arrive before GA4 is ready would otherwise be silently
// dropped. Hold a small, bounded FIFO of pre-ready emits and flush it once analytics goes ready, so
// early first-MFE-view telemetry isn't lost. Bounded so a remote firing pre-ready can't grow memory.
const PRE_READY_BUFFER_MAX = 20;
interface PendingEvent {
	name: string;
	params?: EventParams;
	pageLocation?: string;
}
type HeldEvent = PendingEvent & { back?: number };
// Each carries the user_id it was emitted under, so a later login can't relabel it.
let preReadyBuffer: (PendingEvent & { userId: string | null })[] = [];

// Remote events wait for the shell's first auth answer so a signed-in first view isn't 'anon'.
let authSettled = false;
let authHold: HeldEvent[] = [];

/** crypto.randomUUID is exposed only in secure (https) contexts; local dev + tests run over http,
 *  so fall back there. The id only needs to be unique per emit for downstream dedupe. */
const newEventId = (): string => {
	try {
		return crypto.randomUUID();
	} catch {
		return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
	}
};

declare global {
	interface Window {
		/** Injected by react-native-webview when the page runs inside the RVLife mobile app. */
		ReactNativeWebView?: { postMessage: (message: string) => void };
		rvlifeIgnorePopstate?: boolean;
	}
}

/** GA4 posture. `relay_only` when the Profile shell is embedded in the RVLife mobile app's WebView
 *  (react-native-webview injects `window.ReactNativeWebView`); `standalone` otherwise. Nested, the
 *  mobile app is the analytics authority (its own Firebase/TrackingAggregator), so a Profile-side
 *  gtag would double-count the session across properties — we forward the intent to the host and
 *  emit no gtag of our own. */
export type AnalyticsHostMode = 'standalone' | 'relay_only';

export const getAnalyticsHostMode = (): AnalyticsHostMode =>
	typeof window !== 'undefined' && window.ReactNativeWebView ? 'relay_only' : 'standalone';

const CAMPAIGN_PARAMS: ReadonlySet<string> = new Set([
	'utm_source',
	'utm_medium',
	'utm_campaign',
	'utm_term',
	'utm_content',
	'utm_id',
	'utm_source_platform',
	'utm_creative_format',
	'utm_marketing_tactic',
	'gclid',
	'gbraid',
	'wbraid',
	'dclid',
	'srsltid',
	'fbclid',
	'msclkid',
	'ttclid',
	'twclid',
	'li_fat_id',
	'igshid',
	'gad_source',
	'gad_campaignid'
]);
const REPORTING_PARAMS: ReadonlySet<string> = new Set(['source', 'tab', 'slug', 'ref']);
const REPORTING_VALUE = /^[a-z0-9_-]{1,40}$/i;

const isAllowedQueryParam = (key: string, value: string): boolean => {
	if (CAMPAIGN_PARAMS.has(key)) {
		return value.length >= 1 && value.length <= 100 && !value.includes('@');
	}
	if (key === 'openMissingInfoTab') return value === 'true';
	return REPORTING_PARAMS.has(key) && REPORTING_VALUE.test(value);
};

/** The address GA4 may see: no fragment or credentials, and only allow-listed query keys.
 *  Each key is judged on its first occurrence; `href` comes back as-is when nothing is removed. */
export const cleanLocation = (href: string): string => {
	let url: URL;
	try {
		url = new URL(href);
	} catch {
		return href.split(/[?#]/)[0];
	}
	let changed = href.includes('#') || url.username !== '' || url.password !== '';
	const seen = new Set<string>();
	const kept = new URLSearchParams();
	url.searchParams.forEach((value, key) => {
		const first = !seen.has(key);
		seen.add(key);
		if (first && isAllowedQueryParam(key, value)) kept.append(key, value);
		else changed = true;
	});
	if (!changed) return href;
	url.username = '';
	url.password = '';
	url.hash = '';
	url.search = kept.toString();
	return url.toString();
};

let acceptedPageLocation: string | undefined;
let contextPageLocation: string | undefined;
let entryPageViewSent = false;
const warnedDroppedKeys = new Set<string>();

// While Settings is holding a Back press, keep the last accepted address.
const readPageLocation = (): string | undefined => {
	if (typeof window === 'undefined') return undefined;
	if (acceptedPageLocation === undefined || !window.rvlifeIgnorePopstate) {
		acceptedPageLocation = cleanLocation(window.location.href);
	}
	return acceptedPageLocation;
};

const setGtagPageLocation = (location: string | undefined): void => {
	if (location === undefined || location === contextPageLocation) return;
	contextPageLocation = location;
	ReactGA.gtag('set', { page_location: location });
};

/** Gives GA4's auto-collected hits a clean address; call only right before `ReactGA.initialize`. */
export const seedPageLocationContext = (): void => {
	setGtagPageLocation(readPageLocation());
	// gtag defaults every hit's page_referrer to document.referrer, so replace it only when dirty.
	const referrer = typeof document === 'undefined' ? '' : document.referrer;
	const cleanReferrer = cleanLocation(referrer);
	if (cleanReferrer !== referrer) ReactGA.gtag('set', { page_referrer: cleanReferrer });
};

/** Points GA4's auto-collected hits at the current address once GA4 is live in standalone mode. */
export const syncPageLocationContext = (): void => {
	if (!analyticsReady || getAnalyticsHostMode() !== 'standalone') return;
	setGtagPageLocation(readPageLocation());
};

let popstateDispatching = false;
let afterPopstate: (() => void)[] = [];

/** Runs `fn` once every listener of the popstate being handled has run, or now if there is none. */
export const afterPopstateListeners = (fn: () => void): void => {
	if (popstateDispatching) afterPopstate.push(fn);
	else fn();
};

/** Keeps the page context current across hash changes, back/forward and bfcache restores. */
export const attachPageLocationListeners = (): (() => void) => {
	const sync = (): void => afterPopstateListeners(syncPageLocationContext);
	window.addEventListener('popstate', sync);
	window.addEventListener('hashchange', sync);
	window.addEventListener('pageshow', sync);
	return () => {
		window.removeEventListener('popstate', sync);
		window.removeEventListener('hashchange', sync);
		window.removeEventListener('pageshow', sync);
	};
};

// The account API's pre-derived GA4 id (HMAC-SHA256 hex); anything else would poison the join.
const DERIVED_USER_ID = /^[0-9a-f]{64}$/;
let analyticsUserId: string | null = null;

const setGtagUserId = (id: string | null): void => {
	if (getAnalyticsHostMode() === 'standalone') {
		// undefined, not null: gtag sends a null user_id as an empty `uid=` on later hits.
		ReactGA.gtag('set', { user_id: id ?? undefined });
	}
};

const applyAnalyticsUserId = (): void => {
	if (analyticsReady) setGtagUserId(analyticsUserId);
};

/** Set the GA4 `user_id` on later hits; any value but the derived 64-char hex id clears it. */
export const setAnalyticsUserId = (id: unknown): void => {
	const next = typeof id === 'string' && DERIVED_USER_ID.test(id) ? id : null;
	if (next === analyticsUserId) return;
	analyticsUserId = next;
	applyAnalyticsUserId();
};

// Names the nested host also emits natively: its cookie exchange mints `login` on every embedded
// login, and its `register` message handler emits `sign_up`. Exact names only — the other bridged
// events have no native copy; the relay is their only emit.
const RELAY_ONLY_SUPPRESSED_EVENTS = new Set(['login', 'sign_up']);

// Suppressed before any buffer, so these never take a slot another event needs.
const isRelaySuppressed = (name: string): boolean =>
	getAnalyticsHostMode() === 'relay_only' && RELAY_ONLY_SUPPRESSED_EVENTS.has(name);

// The mobile WebView already relays the shared MFEs' functional events as `{ source, data }` on
// window.ReactNativeWebView (rvlife WebViewUtilities). Forward governed analytics on a dedicated
// `rvlife_analytics` source so the host can route it to Firebase; host-side consumption is TECH-2233.
const relayToMobileHost = (name: string, payload: EventParams): boolean => {
	try {
		window.ReactNativeWebView?.postMessage(
			JSON.stringify({ source: 'rvlife_analytics', data: { name, params: payload } })
		);
		return true;
	} catch {
		// The relay must never break the embedded app; drop on any serialization/host error.
		return false;
	}
};

const SESSION_EDGE_EVENTS = new Set(['login', 'logout']);
// The contract's host_app stamp: which app's WebView embeds this shell.
const EMBEDDING_APP = 'rvlife_app';

const deliver = ({ name, params, pageLocation }: PendingEvent): boolean => {
	const payload: EventParams = {
		...params,
		schema_version: SCHEMA_VERSION,
		platform: PLATFORM,
		// Both mark a signed-in session's edge, and each can reach here after the shell's state flips.
		user_auth_state: SESSION_EDGE_EVENTS.has(name) ? 'authed' : userAuthState,
		event_id: newEventId()
	};
	delete payload.page_location;
	delete payload.page_referrer;
	// Embedded in the mobile WebView: forward to the host, never emit our own gtag (no double-count).
	if (getAnalyticsHostMode() === 'relay_only') {
		return relayToMobileHost(name, { ...payload, host_app: EMBEDDING_APP });
	}
	if (pageLocation !== undefined) payload.page_location = pageLocation;
	if (name === 'page_view' && !entryPageViewSent) {
		entryPageViewSent = true;
		if (document.referrer) payload.page_referrer = cleanLocation(document.referrer);
	}
	ReactGA.gtag('event', name, payload);
	return true;
};

let popsRequested = 0;
let popsLanded = 0;
let popHoldTimer: ReturnType<typeof setTimeout> | undefined;
let heldForPop: (HeldEvent & { back: number })[] = [];
const POP_HOLD_MAX_MS = 1000;

const send = (name: string, params?: EventParams, pageLocation = readPageLocation()): boolean => {
	const event = { name, params, pageLocation };
	if (analyticsReady) {
		syncPageLocationContext();
		return deliver(event);
	}
	if (preReadyBuffer.length >= PRE_READY_BUFFER_MAX) return false;
	preReadyBuffer.push({ ...event, userId: analyticsUserId });
	return true;
};

/** Single GA4 emit boundary: buffers until GA4 is ready (see {@link setAnalyticsReady}), then sends
 *  with host-authoritative core dimensions. False when the buffer is full or the relay fails. */
const emit = (name: string, params?: EventParams): boolean => {
	if (isRelaySuppressed(name)) return true;
	if (popsLanded < popsRequested) {
		if (heldForPop.length >= PRE_READY_BUFFER_MAX) return false;
		// Tagged with its Back, so a view sent after a second Back waits for that one to land.
		heldForPop.push({ name, params, back: popsRequested });
		return true;
	}
	return send(name, params);
};

const sendHeld = (landed: number, landedOn = readPageLocation()): void => {
	authHold = authHold.map(({ name, params, back, pageLocation }) =>
		back !== undefined && back <= landed
			? { name, params, pageLocation: landedOn }
			: { name, params, back, pageLocation }
	);
	const due = heldForPop.filter(({ back }) => back <= landed);
	heldForPop = heldForPop.filter(({ back }) => back > landed);
	due.forEach(({ name, params }) => send(name, params, landedOn));
};

const releasePopHold = (landedOn?: string): void => {
	if (popsLanded >= popsRequested) return;
	clearTimeout(popHoldTimer);
	popsLanded = popsRequested;
	sendHeld(popsRequested, landedOn);
	popsRequested = 0;
	popsLanded = 0;
};

const landPop = (landedOn?: string): void => {
	if (popsLanded + 1 >= popsRequested) {
		releasePopHold(landedOn);
		return;
	}
	popsLanded += 1;
	sendHeld(popsLanded, landedOn);
};

// Each popstate's address, read as it fires: two Backs can land before the first is settled.
let popstateLandings: string[] = [];

let heldBackLanding: string | undefined;
// Query re-encoded and sorted, so an address rebuilt by mergePathWithParams still matches.
const addressOf = (url: string): string => {
	const { pathname, searchParams } = new URL(url, window.location.href);
	searchParams.sort();
	const query = searchParams.toString();
	return query ? `${pathname}?${query}` : pathname;
};

const settlePopstate = (): void => {
	popstateDispatching = false;
	heldBackLanding = window.rvlifeIgnorePopstate ? addressOf(window.location.href) : undefined;
	const landings = popstateLandings;
	popstateLandings = [];
	// A Back Settings holds keeps the page already accepted, which readPageLocation returns.
	landings.forEach((href) =>
		landPop(window.rvlifeIgnorePopstate ? undefined : cleanLocation(href))
	);
	const queued = afterPopstate;
	afterPopstate = [];
	queued.forEach((fn) => fn());
};

if (typeof window !== 'undefined') {
	// A held Back that never lands must not stamp its views with a page the member moves on to.
	// A state-only write (Next.js syncs its router state in place) moves to no page.
	(['pushState', 'replaceState'] as const).forEach((method) => {
		const write = window.history[method].bind(window.history);
		window.history[method] = (...args: Parameters<History['pushState']>): void => {
			const [, , url] = args;
			if (url != null && addressOf(String(url)) !== addressOf(window.location.href)) {
				releasePopHold();
				heldBackLanding = undefined;
			}
			write(...args);
		};
	});
	// Held events would die with the page, so a page being left sends them with what it knows.
	window.addEventListener('pagehide', () => {
		releasePopHold();
		const held = authHold;
		authHold = [];
		if (analyticsReady) held.forEach(deliver);
	});
	// Registered at import, ahead of Next.js's listener, so it sees each popstate before the app.
	window.addEventListener(
		'popstate',
		() => {
			popstateLandings.push(window.location.href);
			if (popstateDispatching) return;
			popstateDispatching = true;
			setTimeout(settlePopstate, 0);
		},
		true
	);
}

const pageAcceptedListeners = new Set<() => void>();

/** Calls `fn` whenever the shell accepts the page already in the address bar as a navigation. */
export const onPageAccepted = (fn: () => void): (() => void) => {
	pageAcceptedListeners.add(fn);
	return () => {
		pageAcceptedListeners.delete(fn);
	};
};

/** Reports the address bar's page: an MFE's Back that the browser had already made. */
export const acceptCurrentPage = (): void => pageAcceptedListeners.forEach((fn) => fn());

/** True, once, when a Back that Settings held has already left the browser on `path`. */
export const takeHeldBackLanding = (path: string): boolean => {
	const landed = heldBackLanding !== undefined && heldBackLanding === addressOf(path);
	if (landed) heldBackLanding = undefined;
	return landed;
};

/** Holds events until a pending history.back() lands, so they carry the address it lands on. */
export const holdUntilPopstate = (): void => {
	popsRequested += 1;
	clearTimeout(popHoldTimer);
	popHoldTimer = setTimeout(releasePopHold, POP_HOLD_MAX_MS);
};

const emitRemote = (name: string, params?: EventParams): boolean => {
	// A session edge is stamped authed anyway, and a boot-time loss reloads before auth settles.
	if (authSettled || isRelaySuppressed(name) || SESSION_EDGE_EVENTS.has(name)) {
		return emit(name, params);
	}
	if (authHold.length >= PRE_READY_BUFFER_MAX) return false;
	// During a pending Back the page is read when that Back lands.
	authHold.push(
		popsLanded < popsRequested
			? { name, params, back: popsRequested }
			: { name, params, pageLocation: readPageLocation() }
	);
	return true;
};

/** Releases remote events held for the shell's first auth answer; false discards them (tests). */
export const setAnalyticsAuthSettled = (settled: boolean): void => {
	authSettled = settled;
	const held = authHold;
	authHold = [];
	if (!settled) return;
	// Already bounded by the hold, so these skip the pre-ready cap rather than vanish at handoff.
	held.forEach(({ back, ...event }) => {
		if (back !== undefined) heldForPop.push({ ...event, back });
		else if (analyticsReady) deliver(event);
		else preReadyBuffer.push({ ...event, userId: analyticsUserId });
	});
};

/** Mark GA4 ready/unavailable so {@link emit} works. Flips true after init and flushes any events
 *  buffered while not-ready; flipping false discards the buffer so a later ready can't replay stale
 *  events (only relevant to tests — production sets ready true exactly once). */
export const setAnalyticsReady = (ready: boolean): void => {
	analyticsReady = ready;
	if (!ready) {
		preReadyBuffer = [];
		acceptedPageLocation = undefined;
		contextPageLocation = undefined;
		entryPageViewSent = false;
		popsRequested = 0;
		popsLanded = 0;
		heldForPop = [];
		clearTimeout(popHoldTimer);
		heldBackLanding = undefined;
		popstateDispatching = false;
		afterPopstate = [];
		popstateLandings = [];
		authHold = [];
		warnedDroppedKeys.clear();
		return;
	}
	const buffered = preReadyBuffer;
	preReadyBuffer = [];
	// Each buffered hit goes out under the id it was emitted with, not one set after it.
	let sentUserId: string | null = null;
	buffered.forEach(({ userId, ...event }) => {
		if (userId !== sentUserId) setGtagUserId(userId);
		sentUserId = userId;
		deliver(event);
	});
	if (analyticsUserId !== sentUserId) applyAnalyticsUserId();
};

/** True for id-like segments — numeric, UUID, long hex, or a long digit-bearing token with no
 *  readable word (3+ consecutive letters) — that `toVirtualPath` strips. Human slugs (which contain
 *  a word, e.g. `2024-rally`, `f150-setup`) and short static names like `v2`/`parks` are kept. */
const isIdSegment = (seg: string): boolean =>
	/^\d+$/.test(seg) ||
	/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(seg) ||
	/^[0-9a-f]{16,}$/i.test(seg) ||
	(seg.length >= 8 && /\d/.test(seg) && !/[a-z]{3}/i.test(seg));

/** Virtual path with `?search`/`#hash` and id-like segments stripped, so page_view paths stay
 *  low-cardinality and free of ids/PII. */
const toVirtualPath = (path: string): string => {
	const pathname = path.split(/[?#]/)[0];
	const segments = pathname.split('/').filter((s) => s.length > 0 && !isIdSegment(s));
	return `/${segments.join('/')}`;
};

const ROUTE_GROUP_MFE: Record<string, string> = {
	auth: 'rv_life_auth',
	settings: 'rv_life_settings',
	garage: 'rv_life_garage'
};
// Only the remotes this shell mounts take part in the surface back-fill, so an arbitrary bridged
// mfe_name can't grow the per-MFE map.
const MOUNTED_MFE_NAMES: ReadonlySet<string> = new Set(Object.values(ROUTE_GROUP_MFE));

/** Host-authoritative page_view for `path` (id-stripped virtual path). A route that mounts an MFE
 *  stamps that MFE's `mfe_name` onto the `page_view`; the surface-level `mfe_view` (with
 *  `surface_id`) is reported by the MFE itself via the DOM bridge, which then back-fills that
 *  surface onto the MFE's later interaction events. `page_view` never carries one. */
export const trackPageView = (path: string): boolean => {
	// getRouteGroup's matchers anchor to end-of-string, so a raw `?query`/`#hash` makes static routes
	// (e.g. /settings) fail to match and silently drops the mfe_name stamp. Match on the bare
	// pathname — not `pagePath`, whose id-segment stripping would itself distort route matching.
	const pathname = path.split(/[?#]/)[0];
	const pagePath = toVirtualPath(pathname);
	const routeGroup = getRouteGroup(pathname);
	const mfeName = routeGroup ? ROUTE_GROUP_MFE[routeGroup] : undefined;
	// The shell reports a page only once the address shows it, so a pending Back must not hold it.
	return send('page_view', { page_path: pagePath, ...(mfeName ? { mfe_name: mfeName } : {}) });
};

// The shell's own mfe_name, matching the ROUTE_GROUP_MFE vocabulary the remotes self-report under,
// so host-originated events slice by surface alongside them.
const HOST_MFE_NAME = 'rv_life_profile';

/** Registered contract event names the shell emits itself, as opposed to bridging from a remote. */
export const HOST_EVENT_NAMES = ['logout', 'notice_shown'] as const;
export type HostEventName = (typeof HOST_EVENT_NAMES)[number];

/** Emit a host-originated product event, stamped with the shell's own `mfe_name`. */
export const trackEvent = (name: HostEventName, params?: EventParams): void => {
	emit(name, { ...params, mfe_name: HOST_MFE_NAME });
};

/** `detail` shape for the `rvlife_analytics` DOM CustomEvent the shared remotes (auth/settings/
 *  garage) dispatch. `mfe_name` lets a remote self-identify so its events slice by MFE. */
export type RvlifeAnalyticsDetail =
	| { kind: 'view'; surface: string; props?: EventParams; mfe_name?: string }
	| { kind: 'event'; name: string; props?: EventParams; mfe_name?: string };

// GA4 automatically collects / reserves these web event names (Enhanced Measurement + reserved). A
// remote emitting one would corrupt GA4's built-in reports, so the bridge drops them. Other names —
// including unregistered or typo'd ones — fail open: canonical-name validation is the contract's
// governance job, not a runtime gate.
const RESERVED_EVENT_NAMES = new Set([
	'page_view',
	'screen_view',
	'scroll',
	'click',
	'first_visit',
	'first_open',
	'session_start',
	'user_engagement',
	'form_start',
	'form_submit',
	'file_download',
	'view_search_results',
	'video_start',
	'video_progress',
	'video_complete',
	'error',
	'app_exception'
]);

// GA4 also reserves these event-name prefixes (`ga_`, `google_`, `firebase_`, leading `_`); names
// carrying them are silently dropped or corrupt built-in reports just like the exact names above, so
// the bridge rejects them too. (Format/length validation stays the contract's governance job.)
const hasReservedPrefix = (name: string): boolean => /^(?:ga_|google_|firebase_|_)/i.test(name);

// Rolling-window backstop against a buggy/hostile remote firing unbounded events (GA4 quota +
// network flood): at most MAX_BRIDGED_EVENTS_PER_WINDOW forwards per BRIDGE_WINDOW_MS. A burst is
// bounded, but a long legitimate session keeps flowing because the count resets each window.
const MAX_BRIDGED_EVENTS_PER_WINDOW = 1000;
const BRIDGE_WINDOW_MS = 60_000;

// The host sanitizes its own page_path via toVirtualPath, but bridged `props` come straight from an
// untrusted remote. Match that same trust boundary on the payload: drop credential-ish keys, run any
// `*_path` value through toVirtualPath (a remote-supplied page_path would otherwise reach GA4 raw,
// bypassing the cardinality/PII stripping), and bound value length. `surface_id` is dropped too: the
// bridge stamps it itself (`d.surface` on a view, the last forwarded view on an event), so a remote
// can neither fake nor clear it. Core dims are already made authoritative in emit(); this guards
// everything else.
const SENSITIVE_KEY = /token|secret|password|auth|email|user_id|credential/i;
// Descriptive keys that name how a member signed in, never a credential.
const ALLOWED_AUTH_KEYS: ReadonlySet<string> = new Set([
	'auth_method',
	'auth_provider',
	'auth_type'
]);
const BRIDGE_STAMPED_KEYS: ReadonlySet<string> = new Set(['surface_id', 'previous_surface']);
const MAX_PROP_VALUE_LENGTH = 100;
// Bounded, so a remote minting endless credential-like keys can't grow memory or flood the console.
const MAX_WARNED_DROPPED_KEYS = 20;
const isDroppedKey = (key: string): boolean => {
	if (BRIDGE_STAMPED_KEYS.has(key)) return true;
	if (ALLOWED_AUTH_KEYS.has(key) || !SENSITIVE_KEY.test(key)) return false;
	if (!warnedDroppedKeys.has(key) && warnedDroppedKeys.size < MAX_WARNED_DROPPED_KEYS) {
		warnedDroppedKeys.add(key);
		// Names only the matched word: the key itself is remote-supplied and may carry PII.
		const matched = key.match(SENSITIVE_KEY)?.[0].toLowerCase();
		// eslint-disable-next-line no-console
		console.warn(`[analytics] dropped bridged prop: its name contains "${matched}"`);
	}
	return true;
};
const sanitizeValue = (key: string, value: unknown): unknown => {
	if (typeof value !== 'string') return value;
	const stripped = key.endsWith('_path') ? toVirtualPath(value) : value;
	return stripped.length > MAX_PROP_VALUE_LENGTH
		? stripped.slice(0, MAX_PROP_VALUE_LENGTH)
		: stripped;
};
const sanitizeBridgedProps = (props: EventParams): EventParams =>
	Object.entries(props)
		.filter(([key]) => !isDroppedKey(key))
		.reduce<EventParams>(
			(clean, [key, value]) => ({ ...clean, [key]: sanitizeValue(key, value) }),
			{}
		);

// GA4 truncates parameter values past 100 characters; a longer surface is never remembered.
const MAX_SURFACE_LENGTH = 100;

/**
 * Forward `rvlife_analytics` DOM CustomEvents from the shared remotes to GA4 via {@link emit}, so the
 * shell stays the sole GA4 client. `detail` is untrusted — every forward is isolated. Returns an
 * unsubscribe.
 */
export const attachAnalyticsBridge = (): (() => void) => {
	// performance.now() (monotonic), not Date.now(): the window is an elapsed-time measure, so a
	// wall-clock adjustment must not shrink or skip it. capLogged is sticky — warn once, then
	// rate-limit silently.
	let windowStart = performance.now();
	let forwarded = 0;
	let capLogged = false;
	// Interaction events carry no surface of their own; only the view sees it. Keyed by mfe_name so
	// a remote inherits its own last-viewed surface, never another MFE's.
	const lastSurfaceByMfe = new Map<string, string>();
	let lastViewedSurface: string | undefined;
	const atCap = (): boolean => {
		const now = performance.now();
		if (now - windowStart >= BRIDGE_WINDOW_MS) {
			windowStart = now;
			forwarded = 0;
		}
		if (forwarded < MAX_BRIDGED_EVENTS_PER_WINDOW) return false;
		if (!capLogged) {
			capLogged = true;
			console.warn(
				'[analytics] rvlife_analytics bridge hit its rate cap; dropping remote events until the window resets'
			);
		}
		return true;
	};
	const listener = (event: Event): void => {
		// `detail` comes from an untrusted remote — a throwing getter or Proxy trap would otherwise
		// reach window.onerror/Bugsnag. Isolate the whole forward so the host is never affected.
		try {
			const { detail } = event as CustomEvent<unknown>;
			if (!detail || typeof detail !== 'object' || Array.isArray(detail)) return;
			const d = detail as Partial<RvlifeAnalyticsDetail>;
			const props =
				d.props && typeof d.props === 'object' && !Array.isArray(d.props)
					? sanitizeBridgedProps(d.props)
					: undefined;
			const mfeName =
				typeof d.mfe_name === 'string' && d.mfe_name ? { mfe_name: d.mfe_name } : undefined;
			const surfaceKey =
				mfeName && MOUNTED_MFE_NAMES.has(mfeName.mfe_name) ? mfeName.mfe_name : undefined;

			if (d.kind === 'view') {
				const { surface } = d;
				if (typeof surface !== 'string') return;
				if (atCap()) return;
				const accepted = emitRemote('mfe_view', {
					...props,
					surface_id: surface,
					...mfeName,
					...(lastViewedSurface !== undefined
						? { previous_surface: lastViewedSurface }
						: {})
				});
				const rememberable = surface.length > 0 && surface.length <= MAX_SURFACE_LENGTH;
				if (accepted) lastViewedSurface = rememberable ? surface : undefined;
				if (accepted && surfaceKey && rememberable) {
					lastSurfaceByMfe.set(surfaceKey, surface);
				}
				forwarded++;
			} else if (d.kind === 'event' && typeof d.name === 'string') {
				if (RESERVED_EVENT_NAMES.has(d.name) || hasReservedPrefix(d.name)) return;
				if (atCap()) return;
				const lastSurface = surfaceKey ? lastSurfaceByMfe.get(surfaceKey) : undefined;
				emitRemote(d.name, {
					...props,
					...(lastSurface !== undefined ? { surface_id: lastSurface } : {}),
					...mfeName
				});
				forwarded++;
			}
		} catch {
			// An untrusted remote's payload must never break the host; drop it.
		}
	};
	document.addEventListener('rvlife_analytics', listener);
	return () => document.removeEventListener('rvlife_analytics', listener);
};
